import { FormEvent, ReactNode, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

interface Props {
  title: ReactNode;
  icon?: string;
  onClose: () => void;
  footer?: ReactNode;
  children: ReactNode;
  bodyStyle?: React.CSSProperties;
  dialogStyle?: React.CSSProperties;
  large?: boolean;
  // When set, the modal wraps its header/body/footer in a <form> so submit/Enter works.
  onSubmit?: (e: FormEvent) => void;
  formClassName?: string;
  subHeader?: ReactNode;
  // Scrolls the modal back to the top whenever this value changes. Pass the
  // current step in a multi-step modal so each step starts at the top.
  scrollResetKey?: unknown;
}

// Number of open modals, so the body scroll lock is released only when the last
// one closes (modals can briefly overlap during transitions / confirm dialogs).
let openModalCount = 0;

// Locks the page scroll while a modal is open by adding Bootstrap's `.modal-open`
// class to <body> (`.modal-open { overflow: hidden }`). Combined with the
// overlay's own `overflow-y: auto`, this yields a single scrollbar — the dialog
// grows to fit its content and the overlay scrolls, matching the Angular modals.
function useBodyScrollLock() {
  useEffect(() => {
    openModalCount += 1;
    document.body.classList.add('modal-open');
    return () => {
      openModalCount -= 1;
      if (openModalCount === 0) document.body.classList.remove('modal-open');
    };
  }, []);
}

// Bootstrap-style modal shell with a backdrop. Click on the backdrop closes;
// click inside the dialog does not. Caller controls open/close via parent state.
// Renders via portal into document.body to avoid z-index and aria-hidden issues
// with Angular's container div.
export function Modal({ title, icon, onClose, footer, children, bodyStyle, dialogStyle, large, onSubmit, formClassName, subHeader, scrollResetKey }: Props) {
  useBodyScrollLock();

  // The overlay is the scroll container; reset it to the top on step changes.
  const overlayRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (overlayRef.current) overlayRef.current.scrollTop = 0;
  }, [scrollResetKey]);
  const inner = (
    <div className="modal-content">
      <div className="modal-header">
        <button type="button" className="close" onClick={onClose} aria-label="Close">&times;</button>
        <h4 className="modal-title">
          {icon && <><i className={icon} aria-hidden="true" />{' '}</>}
          {title}
        </h4>
      </div>
      {subHeader && <div className="modal-sub-header">{subHeader}</div>}
      <div className="modal-body" style={bodyStyle}>
        {children}
      </div>
      {footer && <div className="modal-footer">{footer}</div>}
    </div>
  );
  const dialogClassName = `modal-dialog${large ? ' modal-lg iio-large-modal' : ''}`;
  const modal = (
    <div
      ref={overlayRef}
      className="modal"
      style={{ display: 'block', overflowY: 'auto', background: 'rgba(0,0,0,0.5)' }}
      onClick={onClose}
    >
      <div className={dialogClassName} style={dialogStyle} onClick={e => e.stopPropagation()}>
        {onSubmit ? (
          <form
            onSubmit={e => { e.preventDefault(); onSubmit(e); }}
            className={formClassName}
            noValidate
          >
            {inner}
          </form>
        ) : inner}
      </div>
    </div>
  );
  return createPortal(modal, document.body);
}
